import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { MINT_MIST } from '../../constants/brandSurfaces';

const blue = '#0d4efe';
const ink = '#1a1a2e';
const muted = '#5a6478';

/**
 * The current trade's color, set as `--trade` on FlowCard (brand blue on /find-a-pro), so
 * headers, progress and hover states take the trade's tint: green for landscaping, amber for
 * electrical. Text stays ink for contrast; the trade color is only for fills and borders.
 */
const trade = `var(--trade, ${blue})`;
const tint = (percent) => `color-mix(in srgb, ${trade} ${percent}%, #ffffff)`;

export const FlowPage = styled(Box)({
  minHeight: '70vh',
  background: MINT_MIST,
  padding: '32px 16px 56px',
  '@media (max-width: 600px)': {
    padding: '20px 16px 40px',
  },
});

export const FlowCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$wide',
})(({ $wide }) => ({
  maxWidth: $wide ? 920 : 760,
  margin: '0 auto',
  background: '#ffffff',
  borderRadius: 16,
  border: '1px solid rgba(26, 26, 46, 0.07)',
  boxShadow: '0 12px 32px rgba(26, 26, 46, 0.06)',
  overflow: 'hidden',
}));

export const FlowHeader = styled(Box)({
  padding: '18px 24px 16px',
  background: tint(7),
  borderBottom: `1px solid ${tint(20)}`,
  '@media (max-width: 600px)': {
    padding: '14px 16px 14px',
  },
});

export const FlowHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
});

export const BackButton = styled(ButtonBase)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '6px 10px 6px 6px',
  borderRadius: 10,
  fontFamily: 'inherit',
  fontSize: '0.88rem',
  fontWeight: 600,
  color: muted,
  '&:hover, &.Mui-focusVisible': {
    background: tint(14),
    color: ink,
  },
  '& svg': { fontSize: 18 },
});

export const ProgressLabel = styled('span')({
  fontSize: '0.86rem',
  fontWeight: 600,
  color: muted,
});

export const ProgressTrack = styled(Box)({
  marginTop: 12,
  height: 6,
  borderRadius: 999,
  background: tint(18),
  overflow: 'hidden',
});

export const ProgressFill = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$percent',
})(({ $percent }) => ({
  height: '100%',
  width: `${$percent}%`,
  borderRadius: 999,
  background: trade,
  transition: 'width 0.25s ease',
}));

export const AnswerChipRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 12,
});

export const AnswerChip = styled(ButtonBase)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  borderRadius: 999,
  background: '#ffffff',
  border: `1px solid ${tint(35)}`,
  fontFamily: 'inherit',
  fontSize: '0.84rem',
  fontWeight: 600,
  color: ink,
  '& svg': { fontSize: 14, color: muted },
  '&:hover, &.Mui-focusVisible': {
    borderColor: trade,
  },
});

export const FlowBody = styled(Box)({
  padding: '24px 24px 28px',
  '@media (max-width: 600px)': {
    padding: '18px 16px 22px',
  },
});

export const QuestionTitle = styled('h1')({
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: '1.35rem',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 4px',
  '@media (max-width: 600px)': {
    fontSize: '1.15rem',
  },
});

export const QuestionHint = styled('p')({
  fontSize: '0.92rem',
  color: muted,
  margin: '0 0 18px',
});

export const OptionGroup = styled(Box)({
  '& + &': { marginTop: 18 },
});

export const OptionGroupTitle = styled('h2')({
  fontSize: '0.95rem',
  fontWeight: 700,
  color: ink,
  margin: '0 0 10px',
});

export const OptionGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 10,
});

/** Task step: short text-only tiles, 4 across on desktop, 2 on phones. */
export const TaskGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: 8,
  '@media (max-width: 860px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
  '@media (max-width: 600px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
});

export const OptionButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== '$muted',
})(({ $muted }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 12,
  width: '100%',
  minHeight: 56,
  padding: '10px 14px',
  borderRadius: 12,
  textAlign: 'left',
  fontFamily: 'inherit',
  fontSize: '0.95rem',
  fontWeight: 600,
  lineHeight: 1.3,
  color: ink,
  background: $muted ? MINT_MIST : '#ffffff',
  border: `1px ${$muted ? 'dashed' : 'solid'} ${$muted ? 'rgba(26, 26, 46, 0.18)' : 'rgba(26, 26, 46, 0.1)'}`,
  transition:
    'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
  '&:hover, &.Mui-focusVisible': {
    borderColor: trade,
    background: tint(6),
    boxShadow: '0 4px 14px rgba(26, 26, 46, 0.08)',
    transform: 'translateY(-1px)',
  },
}));

export const TaskButton = styled(OptionButton)({
  minHeight: 48,
  padding: '10px 14px',
  fontSize: '0.92rem',
  '@media (max-width: 600px)': {
    minHeight: 52,
    padding: '8px 12px',
    fontSize: '0.88rem',
  },
});

export const OptionIcon = styled('span', {
  shouldForwardProp: (prop) => prop !== '$color',
})(({ $color = trade }) => ({
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  borderRadius: 10,
  color: $color,
  background: `color-mix(in srgb, ${$color} 14%, #ffffff)`,
  '& svg': { fontSize: 20 },
}));

export const NoteText = styled('p')({
  margin: '0 0 16px',
  padding: '10px 14px',
  borderRadius: 10,
  background: tint(6),
  border: `1px solid ${tint(20)}`,
  fontSize: '0.9rem',
  color: muted,
  '& strong': { color: ink },
});

export const DescribeField = styled(TextField)({
  marginBottom: 16,
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    background: '#ffffff',
    '&.Mui-focused fieldset': { borderColor: blue },
  },
});

export const SecondaryLinkButton = styled(Button)({
  marginTop: 14,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  color: blue,
});
